import { Box, Flex, Grid, PageWrapper, Paginator, Spacing, useLayout } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { hocs as pageHocs } from 'modules/page/hocs';
import { memo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { useBackHeader } from 'shared/components';
import { getPath, Path } from 'urlBuilder';

import { discardChanges } from '../../resources/actions';
import { ReportPopups, ReportSettings, ReportTable, ReportToolbar } from '../../resources/components';
import { useBreadcrumbs } from '../../resources/hooks/useBreadcrumbs';
import { getPageSafe } from '../../resources/selectors/pageSelectors';
import { pageConfig } from './config';
import { Loading } from './loading';
import { usePageData } from './resources/hooks/usePageData';
import { useReportData } from './resources/hooks/useReportData';
import { messages } from './resources/messages';
import { ContentProps } from './resources/types';

const Content = memo<ContentProps>((props) => {
    const { company } = props;

    const dispatch = useDispatch();

    const { report, reportType, reportConfig, handleReportPageChange } = useReportData();

    const paginatorHidden = report.lastPage && report.pageIndex === 0;

    useBreadcrumbs(reportType);
    useBackHeader(getPath(Path.reportsDashboard, company.id, reportType));

    useMount(() => {
        amplitudeService.sendData('reports: view list');
    });

    useEffect(() => {
        return () => {
            dispatch(discardChanges(true));
        };
    }, [dispatch]);

    if (!reportConfig) {
        return null;
    }

    return (
        <PageWrapper>
            <Helmet title={messages.title({ reportName: reportConfig?.name, companyName: company.displayName })} />

            <PageWrapper.Toolbar>
                <ReportToolbar />

                <Spacing height={16} />

                <ReportSettings />
            </PageWrapper.Toolbar>

            <PageWrapper.Content>
                <Grid overflowX='hidden'>
                    <Box overflowX='auto'>
                        <ReportTable
                            reportConfig={reportConfig}
                            data={report.rows}
                            columns={reportConfig?.columns || []}
                            loading={report.loading}
                        />
                    </Box>
                </Grid>

                <Spacing height={8} />

                {!paginatorHidden && (
                    <Flex container justifyContent='center'>
                        <Paginator
                            currentPageIndex={report.pageIndex}
                            isLastPage={report.lastPage}
                            onPageChange={handleReportPageChange}
                        />
                    </Flex>
                )}

                <ReportPopups />
            </PageWrapper.Content>
        </PageWrapper>
    );
});

export const Page = memo(() => {
    const innerActivePageId = useSelector(selectors.navigation.getInnerActivePageId);
    const activeReportConfig = useSelector(getPageSafe)?.activeReportConfig;

    useLayout({ hasHeader: true, hasDrawer: true });

    const { isInitialLoading, company } = usePageData();

    return isInitialLoading || !company || innerActivePageId !== pageConfig.innerActivePageId || !activeReportConfig ? (
        <Loading />
    ) : (
        <Content company={company} />
    );
});

export default pageHocs.withProtected({ Page, roles: pageConfig.access });
